/**
 * SharedCrewMemberContextMenu - Unified crew member context menu
 * 
 * EF-SHEET-CREW-MEMBER-CONTEXT-MENU-PARITY v1.0
 * 
 * Single source of truth for crew member context menus used by:
 * - TaskCrewPlannerPage (SHIFT Crew Planner / Task view)
 * - SheetPlannerExpandedSection (SHEET Crew Planner)
 * 
 * All menu items, ordering, separators, and enabled/disabled logic match exactly.
 */

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "../ui/context-menu";
import { Pencil, Trash2 } from "lucide-react";

/**
 * CrewMemberContextMenu - Context menu wrapper for crew member actions
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The element to wrap (trigger)
 * @param {Object} props.assignment - Crew assignment object
 * @param {string} props.shiftId - Shift ID for shift-specific actions
 * @param {Function} props.onEditCrew - Handler for Edit Crew action
 * @param {Function} props.onToggleConfirmed - Handler for Confirmed toggle
 * @param {Function} props.onToggleWorked - Handler for Worked toggle
 * @param {Function} props.onSendPWALink - Handler for Send PWA Install Link
 * @param {Function} props.onCopyPWAInstall - Handler for Copy PWA Install
 * @param {Function} props.onEmailCrew - Handler for Email Crew
 * @param {Function} props.onPushCrew - Handler for Push to App
 * @param {Function} props.onToggleCrewChief - Handler for Crew Chief toggle
 * @param {Function} props.onRemove - Handler for Remove action
 * @param {number} [props.scale=1] - Scale factor for sizing (optional)
 */
export function CrewMemberContextMenu({
  children,
  assignment,
  shiftId,
  onEditCrew,
  onToggleConfirmed,
  onToggleWorked,
  onSendPWALink,
  onCopyPWAInstall,
  onEmailCrew,
  onPushCrew,
  onToggleCrewChief,
  onRemove,
  scale = 1,
}) {
  // Derive state from assignment
  const isCrewChief =
    assignment.role?.toLowerCase().includes("crew chief") ||
    assignment.role?.toLowerCase().includes("team leader");
  const isConfirmed = assignment.confirmed === true;
  const isWorked = assignment.worked === true;

  const menuHeight = Math.round(24 * scale);
  const fontSize = Math.round(10 * scale);

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent 
        className="min-w-[160px]" 
        style={{ fontSize: `${fontSize}px` }}
      >
        {/* Edit Crew */}
        <ContextMenuItem 
          onClick={() => onEditCrew?.(assignment.staff_id)} 
          style={{ height: `${menuHeight}px` }}
        >
          <Pencil style={{ width: `${12 * scale}px`, height: `${12 * scale}px`, marginRight: `${4 * scale}px` }} /> 
          Edit Crew
        </ContextMenuItem>
        
        <ContextMenuSeparator />
        
        {/* Confirmed/Worked toggles */}
        <ContextMenuItem 
          onClick={() => onToggleConfirmed?.(shiftId, assignment.staff_id, !isConfirmed)} 
          style={{ height: `${menuHeight}px` }}
        >
          {isConfirmed ? "✔️ Confirmed ✓" : "☐ Confirmed"}
        </ContextMenuItem>
        <ContextMenuItem 
          onClick={() => onToggleWorked?.(shiftId, assignment.staff_id, !isWorked)} 
          style={{ height: `${menuHeight}px` }}
        >
          {isWorked ? "W Worked ✓" : "☐ Worked"}
        </ContextMenuItem>
        
        <ContextMenuSeparator />
        
        {/* PWA / Communication actions */}
        <ContextMenuItem 
          onClick={() => onSendPWALink?.(assignment.staff_id, assignment.staff_name)} 
          style={{ height: `${menuHeight}px` }}
        >
          📲 Send PWA Install Link
        </ContextMenuItem>
        <ContextMenuItem 
          onClick={() => onCopyPWAInstall?.(assignment.staff_id, assignment.staff_name)} 
          style={{ height: `${menuHeight}px` }}
        >
          📋 Copy PWA Install (WhatsApp/SMS)
        </ContextMenuItem>
        <ContextMenuItem 
          onClick={() => onEmailCrew?.(shiftId, assignment.staff_id, assignment.staff_name)} 
          style={{ height: `${menuHeight}px` }}
        >
          ✉️ Email Crew
        </ContextMenuItem>
        <ContextMenuItem 
          onClick={() => onPushCrew?.(shiftId, assignment.staff_id, assignment.staff_name)} 
          style={{ height: `${menuHeight}px` }}
        >
          📱 Push to App
        </ContextMenuItem>
        
        <ContextMenuSeparator />
        
        {/* Crew Chief toggle */}
        <ContextMenuItem 
          onClick={() => onToggleCrewChief?.(assignment.staff_id, !isCrewChief)} 
          style={{ height: `${menuHeight}px` }}
        >
          {isCrewChief ? "Remove Crew Chief" : "Set as Crew Chief"}
        </ContextMenuItem>
        
        <ContextMenuSeparator />
        
        {/* Remove */}
        <ContextMenuItem 
          onClick={() => onRemove?.(assignment.staff_id)} 
          className="text-red-600"
          style={{ height: `${menuHeight}px` }}
        >
          <Trash2 style={{ width: `${12 * scale}px`, height: `${12 * scale}px`, marginRight: `${4 * scale}px` }} /> 
          Remove
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

export default CrewMemberContextMenu;
